/**
 * The frame's shape, as the main process handed it down.
 *
 * Its own module rather than a component export: the interface-scale store and
 * the caption-button scrim both need it, and reaching into the title bar
 * component for it would make those imports circular.
 */

/**
 * Derived from the bridge type rather than restated. `WindowChromeInfo` is
 * declared exactly once — in `electron/windowChrome.ts`, imported by
 * `src/types/electron.d.ts` — so the field list lives in one place (CLAUDE.md:
 * share types across the UI/electron boundary). Deriving through `Window`
 * rather than importing the electron module directly keeps this `.tsx` out of
 * the composite-project reference graph (TS6305).
 */
type WindowChromeInfo = Omit<
  NonNullable<Window['electronAPI']>['windowChrome'],
  'onFullscreenChanged'
>;

/**
 * The window-chrome shell around the main toolbar row (CH-02, CH-29 partial).
 *
 * On macOS (`hiddenInset`) and Windows (`hidden` + `titleBarOverlay`) the app
 * has no OS title bar — the toolbar row *is* the title bar. This component owns
 * only the three things that makes true:
 *
 *  1. the draggable surface (`-webkit-app-region: drag`),
 *  2. the left inset that keeps the first control clear of the macOS traffic
 *     lights — collapsed to 0 in fullscreen, where those buttons auto-hide,
 *  3. the `data-window-*` hooks the stylesheet uses to reserve the Windows
 *     caption-button strip via `env(titlebar-area-*)`.
 *
 * It deliberately does NOT restructure the toolbar's element set — toolbar
 * information architecture (CH-14) is a separate, deferred piece of work, and
 * the Execute button stays in the query-editor toolbar.
 *
 * Everything clickable inside this surface must be `no-drag` — including its
 * pseudo-elements, which do NOT inherit it — or it becomes dead pixels. The
 * blanket rule for that lives in src/index.css next to the `.app-region-drag`
 * utility, scoped to `[data-window-chrome='integrated']`.
 */

const NATIVE_CHROME: WindowChromeInfo = {
  mode: 'native',
  platform: 'browser',
  contentInsetLeft: 0,
  uiScale: 1,
};

/**
 * The frame's shape is fixed for the process lifetime, so this is a plain read
 * rather than a hook with state — no re-render path, no first-paint flash.
 * Falls back to the native frame in browser mode, where there is no preload.
 */
export function getWindowChrome(): WindowChromeInfo {
  return window.electronAPI?.windowChrome ?? NATIVE_CHROME;
}
